import React, { useEffect, useState, useCallback } from "react";
import CustomModal from "../../CustomModal/CustomModal";
import Header from "../Components/Header";
import DataTable, { RowActionsTray } from "../Components/DataTable";
import { fileIcons } from "../Icons";
import { format } from "date-fns";
import {
  formatFileSize,
} from "../utils/utils";
import {
  getFileActions,
} from "../utils/fileActions";
import Loader from "../Components/Loader";
import { useAuth } from "../../../features/auth/AuthContext";
import Pagination from "../Components/Pagination";
import RenameModal from "./RenameModal";
import DeleteModal from "./DeleteModal";
import MoveFile from "./MoveFile";
import FilePreviewModal2 from "../../FilePreview/FilePreviewModal2";
import BreadCrumb from "../Components/BreadCrumb";
import { useFileActions } from "../hooks/useFileActions";
import { useRepoQuery } from "../hooks/useRepoQuery";
import { useModal } from "../hooks/useModal";
import Filelist from "../Components/Mobile/Filelist";
import useBranding from "../../../hooks/useBranding";

const ProfileModal = ({
  open,
  close,
  course = null,
  headLabel = "Upload File",
  type,
  lesson = null,
  id = null,
  onUploadComplete,
  disableUpload = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const { modal, reqData, openModal, closeModal } = useModal();
  const { token } = useAuth();
  const branding = useBranding()

  const [path, setPath] = useState([]);
  const currentFolderId = path?.length > 0 ? path[path.length - 1]?.id : 2;

  const { data, isLoading, isFetching } = useRepoQuery({
    token,
    endpoint: "manual-upload/folder-contents/",
    queryKey: "folderContents",
    folderId: currentFolderId,
    search: searchParam,
    page: currentPage,
  });

  const {
    favourite,
    download,
    openRename,
    openDelete,
    openMove,
    preview,
    deleteFile,
    isFavouriting,
    isDeleting,
    folderDownload,
  } = useFileActions({ token, openModal, closeModal });

  const handlers = {
    favourite,
    openRename,
    openDelete,
    openMove,
    download,
    folderDownload,
  };

  const columns = [
    {
      key: "s3_link",
      title: "Name",
      sortable: true,
      sortAccessor: (row) =>
        row?.item_type === "folder" ? row?.folder_name : row?.file_name,
      render: (val, row, idx) => {
        const FileIcon = fileIcons?.[row?.file_type] || fileIcons?.["folder"];
        return (
          <button
            // onClick={() => folderClick(row)}
            className="border-none bg-transparent flex items-center gap-2 w-full"
          >
            <span>{FileIcon && <FileIcon />}</span>
            <span className="truncate text-[#555555] font-normal text-[0.8rem]">
              {row?.item_type === "folder" ? row?.folder_name : row?.file_name}
            </span>
          </button>
        );
      },
    },
    {
      key: "upload_type",
      title: "",
      render: (val, row) => (
        <div className="file-type">
          {val ? val : row?.item_type === "folder" ? "Generated" : ""}
        </div>
      ),
    },
    {
      key: "full_path",
      title: "Location",
      align: "left",
      render: (val) => (
        <div className="truncate text-[#555555] font-normal text-[0.8rem]">
          {val.replace(/^Manually uploaded\//i, "")}
        </div>
      ),
    },
    {
      key: "updated_at",
      title: "Updated On",
      align: "left",
      sortable: true,
      type: "date",
      render: (val) => (
        <div className="truncate text-[#555555] font-normal text-[0.8rem]">
          {format(new Date(val), "dd/MM/yy")}
        </div>
      ),
    },
    {
      key: "file_size",
      title: "Size",
      align: "left",
      sortable: true,
      render: (val, row, idx) => (
        <span className="truncate text-[#555555] font-normal text-[0.8rem]">
          {formatFileSize(row?.["file_size"])}
        </span>
      ),
    },
    {
      key: "original_name",
      title: "",
      align: "left",
      render: (val, row, idx, isHovered) => {
        let rowActions = getFileActions({ row, handlers });
        return (
          <div
            style={{
              position: "relative",
              overflow: "visible",
              width: 5 * 28 + 10,
              minWidth: 5 * 28 + 10,
              // width:'100%',
              padding: "0 8px",
            }}
          >
            <RowActionsTray
              actions={rowActions}
              row={row}
              visible={isHovered}
            />
          </div>
        );
      },
    },
  ];

  function folderClick(clickedFolder) {

    // lesson_id wins over course_id; null/undefined-safe (?? keeps a valid 0 id)
    const entityId = clickedFolder?.lesson_id ?? clickedFolder?.course_id ?? null;
    setCurrentPage(1);

    setPath((prev) => {
      // both null -> carry forward entity_id from the parent breadcrumb
      const inheritedEntityId = prev.length ? prev[prev.length - 1]?.entity_id ?? null : null;

      const newPath = {
        id: clickedFolder?.id,
        name: clickedFolder?.folder_name,
        entity_id: entityId ?? inheritedEntityId,
      };

      return [...prev, newPath];
    });
  }
  function handleBreadCrumClick(folder, index) {
    const newPath = path.slice(0, index + 1);
    setPath(newPath);
    // setCurrentFolder(folder);
  }

  async function onPageChange(page) {
    // getData(page);
    setCurrentPage(page);
  }

  useEffect(() => {
    const description = course?.path_description || [];

    setPath(
      description.map((item, index) =>
        index === 1 ? { ...item, entity_id: course?.course_id ?? null } : item
      )
    );
  }, [course?.path_description, course?.course_id]);
  const handleSearchChange = useCallback(
    (input) => {
      if (input === searchParam) return;
      setSearchParam(input);
      setCurrentPage(1);
    },
    [searchParam],
  );

  return (
    <>
      <CustomModal
        childMT={2}
        title={branding?.filename || "My Files"}
        width={800}
        onClose={close}
        open={open}
        height={"90vh"}
        storage={data?.storage}
      >
        <Header
          searchParam={searchParam}
          setSearchParam={handleSearchChange}
          title={headLabel}
          searchPlaceholder="Search files or folders"
          isModal={true}
          onUploadComplete={onUploadComplete}
          currentFolder={path?.length > 0 ? path?.[path.length - 1] : null}
          type={type}
          currentLessonFolder={lesson?.folder_id}
          id={id}
          path={path}
          data={data}
          forceDisableUpload={disableUpload}
        />

        {path?.length > 0 && (
          <BreadCrumb onFolderClick={handleBreadCrumClick} path={path} />
        )}
        {isLoading || isFavouriting || isFetching ? (
          <Loader />
        ) : (
          <>
            {window.innerWidth < 640 ? (
              <Filelist
                data={data?.all}
                filePreview={preview}
                handleDelete={openDelete}
                changeFav={favourite}
                folderClick={folderClick}
                handleMove={openMove}
                handleRename={openRename}
              />
            ) : (
              <DataTable
                columns={columns}
                dataSource={data?.all}
                rowKey="key"
                maxHeight="max-h-[330px]"
                emptyText="No Files found"
                onRow={(row) => ({
                  onClick: () => {
                    if (row?.item_type === "folder") {
                      folderClick?.(row);
                    } else {
                      preview(row);
                    }
                  },
                })}
              />
            )}
          </>
        )}
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalCount={data?.count}
        />
      </CustomModal>
      <RenameModal
        open={modal === "rename"}
        close={closeModal}
        file={reqData}
      />
      <DeleteModal
        open={modal === "delete"}
        close={closeModal}
        handleDelete={() => deleteFile(reqData, "soft")}
        fileName={reqData?.file_name}
        loading={isDeleting}
      />
      <FilePreviewModal2
        isOpen={modal === "filePreview" ? true : false}
        file={reqData}
        onClose={closeModal}
      />
      <MoveFile
        open={modal === "moveFile" ? true : false}
        file={reqData}
        close={closeModal}
      />
    </>
  );
};

export default ProfileModal;